import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router';
import { Property, nearbyPlaces } from '../../data/mockData';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { ArrowLeft, Save, MapPin } from 'lucide-react';
import { useProperties } from '../../contexts/PropertyContext';
import { useCategories } from '../../contexts/CategoriesContext';
import { toast } from 'sonner';
import { uploadImage } from '../../lib/supabaseStorage';

const egyptianCities = [
  'القاهرة', 'الجيزة', 'الإسكندرية', 'الدقهلية', 'الشرقية', 'الغربية', 'المنوفية', 'القليوبية',
  'الفيوم', 'بني سويف', 'المنيا', 'أسيوط', 'سوهاج', 'قنا', 'الأقصر', 'أسوان', 'البحيرة',
  'كفر الشيخ', 'الإسماعيلية', 'السويس', 'بورسعيد', 'دمياط', 'العريش', 'طابا', 'شرم الشيخ',
  'الغردقة', 'العين السخنة', '6 أكتوبر', 'الشيخ زايد', 'مدينة نصر', 'المعادي', 'التجمع الخامس',
  'التجمع الأول', 'حلوان', 'شبرا', 'العباسية', 'الدقي', 'المهندسين', 'الزمالك', 'مصر الجديدة',
  'هيليوبوليس', 'الرحاب', 'مدينة الشروق', 'بدر', 'العاصمة الإدارية'
];

export default function AddProperty() {
  const navigate = useNavigate();
  const { addProperty } = useProperties();
  const { categories, loading: categoriesLoading } = useCategories();
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    price: '',
    area: '',
    bedrooms: '',
    bathrooms: '',
    garages: '',
    floor: '',
    location: '',
    type: 'sale' as 'sale' | 'rent',
    category: '', // Will be set when categories load
    description: '',
    videoUrl: '',
    adminNote: '',
    featured: false,
    active: true, // New properties are active by default
    mainImage: '',
    galleryImages: [] as string[],
    nearby: [] as string[],
    mapsUrl: ''
  });

  // Set default category when categories load
  useEffect(() => {
    if (categories.length > 0 && !formData.category) {
      setFormData(prev => ({ ...prev, category: categories[0].name }));
    }
  }, [categories, formData.category]);
  const [locationInput, setLocationInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const locationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (locationRef.current && !locationRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (field: string, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMainImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const localUrl = URL.createObjectURL(file);
      handleInputChange('mainImage', localUrl);
      
      try {
        setSaving(true);
        const imageUrl = await uploadImage(file);
        console.log('Main image uploaded to Supabase:', imageUrl);
        handleInputChange('mainImage', imageUrl);
        toast.success('تم رفع الصورة الرئيسية بنجاح!');
      } catch (error) {
        console.error('Error uploading main image:', error);
        toast.error('فشل رفع الصورة على الخادم، سيتم عرضها محلياً');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleGalleryImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const localUrls = files.map(file => URL.createObjectURL(file));
    const prevGallery = formData.galleryImages;
    
    handleInputChange('galleryImages', [...prevGallery, ...localUrls]);

    try {
      setSaving(true);
      const uploadPromises = files.map(file => uploadImage(file));
      const urls = await Promise.all(uploadPromises);

      console.log('Gallery images uploaded to Supabase:', urls);
      handleInputChange('galleryImages', [...prevGallery, ...urls]);
      toast.success(`تم رفع ${files.length} صور بنجاح!`);
    } catch (error) {
      console.error('Error uploading gallery images:', error);
      toast.error('فشل رفع بعض الصور على الخادم، سيتم عرضها محلياً');
    } finally {
      setSaving(false);
    }
  };

  const removeGalleryImage = (index: number) => {
    const updatedGallery = formData.galleryImages.filter((_, i) => i !== index);
    handleInputChange('galleryImages', updatedGallery);
  };

  const toggleNearby = (placeId: string) => {
    setFormData(prev => ({
      ...prev,
      nearby: prev.nearby.includes(placeId)
        ? prev.nearby.filter(id => id !== placeId)
        : [...prev.nearby, placeId]
    }));
  };

  const filteredCities = egyptianCities.filter(city => 
    city.toLowerCase().includes(locationInput.toLowerCase())
  );

  const handleLocationSelect = (city: string) => {
    handleInputChange('location', city);
    setLocationInput(city);
    setShowSuggestions(false);
  };

  const handleSave = async () => {
    // Validate price is reasonable (max 100 billion)
    const priceValue = parseFloat(formData.price) || 0;
    if (priceValue > 100000000000) {
      toast.error('السعر كبير جداً. أقصى قيمة مسموح بها هي 100 مليار جنيه');
      return;
    }
    if (priceValue <= 0) {
      toast.error('يرجى إدخال سعر صحيح');
      return;
    }

    setSaving(true);

    try {
      // Generate a proper UUID v4
      const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = Math.random() * 16 | 0;
          const v = c === 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      };

      // Find the category ID for the selected category name
      const selectedCategory = categories.find(cat => cat.name === formData.category);
      
      const newProperty: Property = {
        id: generateUUID(),
        title: formData.title,
        price: parseInt(formData.price) || 0,
        area: parseInt(formData.area) || 0,
        bedrooms: parseInt(formData.bedrooms) || 0,
        bathrooms: parseInt(formData.bathrooms) || 0,
        garages: parseInt(formData.garages) || 0,
        floor: parseInt(formData.floor) || 1,
        location: formData.location,
        type: formData.type,
        category: formData.category,
        categoryId: selectedCategory?.id || formData.category, // Use category ID if found, fallback to category name
        description: formData.description,
        featured: formData.featured,
        active: formData.active,
        createdAt: new Date().toISOString(),
        facilities: [],
        nearby: formData.nearby,
        videoUrl: formData.videoUrl,
        adminNote: formData.adminNote,
        mainImage: formData.mainImage,
        galleryImages: formData.galleryImages,
        mapsUrl: formData.mapsUrl
      };

      await addProperty(newProperty);
      toast.success('تم إضافة العقار بنجاح!');
      navigate('/admin/properties');
    } catch (error) {
      console.error('Error adding property:', error);
      toast.error('حدث خطأ أثناء إضافة العقار. الرجاء المحاولة مرة أخرى.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/properties">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 ml-2" />
                  العودة
                </Button>
              </Link>
              <h1 className="text-2xl font-bold text-gray-800">إضافة عقار جديد</h1>
            </div>
            <Button onClick={handleSave} className="bg-[#D4AF37] hover:bg-[#B8960C] text-white" disabled={saving}>
              <Save className="w-4 h-4 ml-2" />
              {saving ? 'جاري الحفظ...' : 'إضافة العقار'}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="max-w-4xl mx-auto space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4">معلومات أساسية</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">عنوان العقار</label>
                <Input
                  placeholder="عنوان العقار"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">السعر (ج.م.)</label>
                <Input
                  type="number"
                  placeholder="مثال: 500000 ج.م."
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">المساحة (م²)</label>
                <Input
                  type="number"
                  placeholder="250"
                  value={formData.area}
                  onChange={(e) => handleInputChange('area', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">عدد الغرف</label>
                <Input
                  type="number"
                  placeholder="4"
                  value={formData.bedrooms}
                  onChange={(e) => handleInputChange('bedrooms', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">عدد الحمامات</label>
                <Input
                  type="number"
                  placeholder="3"
                  value={formData.bathrooms}
                  onChange={(e) => handleInputChange('bathrooms', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">عدد المواقف</label>
                <Input
                  type="number"
                  placeholder="2"
                  value={formData.garages}
                  onChange={(e) => handleInputChange('garages', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">الطابق</label>
                <Input
                  type="number"
                  placeholder="1"
                  value={formData.floor}
                  onChange={(e) => handleInputChange('floor', e.target.value)}
                  required
                />
              </div>
              <div ref={locationRef} className="relative">
                <label className="block text-sm font-medium mb-2">الموقع</label>
                <div className="relative">
                  <Input 
                    placeholder="ابحث عن المدينة..." 
                    value={locationInput}
                    onChange={(e) => {
                      setLocationInput(e.target.value);
                      handleInputChange('location', e.target.value);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    required
                    className="pr-10"
                  />
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
                
                {showSuggestions && filteredCities.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                    {filteredCities.map((city, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleLocationSelect(city)}
                        className={`w-full text-right px-4 py-2 hover:bg-gray-100 transition-colors flex items-center gap-2 ${
                          formData.location === city ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : ''
                        }`}
                      >
                        <MapPin className="w-4 h-4" />
                        {city}
                      </button>
                    ))}
                  </div>
                )}
                
                {formData.location && (
                  <div className="mt-3 p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg">
                    <div className="flex items-center gap-2 text-[#8B7109]">
                      <MapPin className="w-5 h-5" />
                      <span className="font-medium">الموقع المحدد:</span>
                    </div>
                    <p className="mt-2 text-gray-800 font-bold text-lg">{formData.location}</p>
                    <p className="mt-1 text-sm text-gray-500">سيتم عرض هذا الموقع في صفحة العقار للزوار</p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">التصنيف</label>
                <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="اختر التصنيف" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoriesLoading ? (
                      <div className="px-4 py-2 text-sm text-gray-500">جاري التحميل...</div>
                    ) : categories.length > 0 ? (
                      categories.map((category) => (
                        <SelectItem key={category.id} value={category.name}>
                          {category.name}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-sm text-gray-500">لا توجد تصنيفات متاحة</div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">نوع العملية</label>
                <Select value={formData.type} onValueChange={(value: 'sale' | 'rent') => handleInputChange('type', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="اختر النوع" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sale">بيع</SelectItem>
                    <SelectItem value="rent">إيجار</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium mb-2">الوصف</label>
              <textarea
                className="w-full px-3 py-2 border rounded-lg"
                rows={4}
                placeholder="وصف العقار..."
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                required
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium mb-2">ملاحظات إدارية (خاصة)</label>
              <textarea
                className="w-full px-3 py-2 border rounded-lg bg-yellow-50 border-yellow-200"
                rows={3}
                placeholder="هذه الملاحظات تظهر للمشرفين فقط ولا تظهر في صفحة العقار العامة..."
                value={formData.adminNote}
                onChange={(e) => handleInputChange('adminNote', e.target.value)}
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium mb-2">رابط الفيديو (يوتيوب، فيسبوك، انستجرام، فيميو، أو رابط مباشر)</label>
              <Input
                placeholder="https://www.youtube.com/watch?v=..."
                value={formData.videoUrl}
                onChange={(e) => handleInputChange('videoUrl', e.target.value)}
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium mb-2">رابط خرائط Google (اختياري)</label>
              <Input
                placeholder="https://maps.google.com/..."
                value={formData.mapsUrl}
                onChange={(e) => handleInputChange('mapsUrl', e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">يمكنك إضافة رابط موقع العقار على Google Maps</p>
            </div>
            <div className="mt-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="featured"
                  checked={formData.featured}
                  onCheckedChange={(checked) => handleInputChange('featured', checked as boolean)}
                />
                <label htmlFor="featured" className="text-sm font-medium">
                  عقار مميز
                </label>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="active"
                  checked={formData.active}
                  onCheckedChange={(checked) => handleInputChange('active', checked as boolean)}
                />
                <label htmlFor="active" className="text-sm font-medium">
                  Active (Visible on website)
                </label>
              </div>
            </div>
          </div>

          {/* Nearby Places */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#D4AF37]" />
              الأماكن القريبة
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {nearbyPlaces.map((place) => (
                <div key={place.id} className="flex items-center gap-2">
                  <Checkbox
                    id={`nearby-${place.id}`}
                    checked={formData.nearby.includes(place.id)}
                    onCheckedChange={() => toggleNearby(place.id)}
                  />
                  <label
                    htmlFor={`nearby-${place.id}`}
                    className="text-sm text-gray-700 cursor-pointer"
                  >
                    {place.name}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4">الصور</h2>

            {/* Main Image Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">الصورة الرئيسية</label>
              <div className="space-y-3">
                {formData.mainImage && (
                  <div className="relative w-full h-48 rounded-lg overflow-hidden border">
                    <img
                      src={formData.mainImage}
                      alt="Main property"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleInputChange('mainImage', '')}
                      className="absolute top-2 left-2 bg-red-500 text-white p-2 rounded-lg hover:bg-red-600"
                    >
                      حذف
                    </button>
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleMainImageUpload}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
            </div>

            {/* Gallery Images Upload */}
            <div>
              <label className="block text-sm font-medium mb-2">صور المعرض</label>
              <div className="space-y-3">
                {formData.galleryImages.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {formData.galleryImages.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={image}
                          alt={`Gallery ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border"
                        />
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(index)}
                          className="absolute top-1 left-1 bg-red-500 text-white p-1 rounded hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleGalleryImageUpload}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">يمكنك اختيار عدة صور</p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
